import React from 'react';
import {
  Badge, Box, Divider, Flex, HStack, Skeleton, Text, Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import { FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { useQuery } from 'react-query';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_BASIX_API || '';

interface DealSummary {
  proposalNumber: string;
  termMonths: number;
  contracts: { id: string; typeLabel: string; title: string; status: number; number?: string }[];
  oneTime: {
    subscriptionSetup: number; switchPilotSetup: number; total: number;
    quoted: number; reconciles: boolean; difference: number;
  };
  monthly: {
    license: number; switchPilot: number; helpDesk: number; helpDeskTier: string; total: number;
  };
  // item 1411 — the generated Help Desk paper read back against the deal
  helpDeskPaper?: { number?: string; tier?: string | null; fee?: number | null; matches: boolean; note?: string | null } | null;
}

const money = (n: number): string => `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// item 1109 (Chad: "link contracts together and show a linked total breakdown of one time vs
// sub") — a deal ships as 2–3 separate agreements, so nothing showed what the customer signed
// up for IN TOTAL. The contracts are already linked by their proposal; this is the combined
// view. It also RECONCILES: the agreements' one-time money must equal the proposal's one-time
// total — the exact invariant that the double-billed SwitchPilot fee and the missing buy-in
// broke, and the fastest way to catch the next one before a customer does.
const DealTotalsCard: React.FC<{ proposalId: string }> = ({ proposalId }) => {
  const { data, isLoading } = useQuery(
    ['deal-summary', proposalId],
    async (): Promise<DealSummary> => {
      const res = await axios.get(`${apiUrl}Contracts/deal-summary/${proposalId}`);
      return res.data;
    },
    { staleTime: 30_000 },
  );
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const bg = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');

  if (isLoading) return <Skeleton height="74px" borderRadius="md" mb={2} />;
  if (!data) return null;
  const { oneTime, monthly } = data;
  // nothing priced (a standard deal with only help-desk paper) — no totals worth showing
  if (oneTime.total <= 0 && monthly.total <= 0) return null;

  const part = (label: string, value: number): React.ReactNode => (value > 0 ? (
    <Text fontSize="xs" color={sub} whiteSpace="nowrap">
      {`${label} ${money(value)}`}
    </Text>
  ) : null);

  return (
    <Box borderWidth="1px" borderColor={border} bg={bg} borderRadius="md" px={3} py={2} mb={2}>
      <Flex align="center" wrap="wrap" sx={{ gap: '8px' }} mb={1}>
        <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase">
          Deal total
        </Text>
        <Text fontSize="xs" color={sub}>{data.proposalNumber}</Text>
        <Badge borderRadius="full" px={2} fontSize="0.6rem">
          {`${data.contracts.length} agreement${data.contracts.length === 1 ? '' : 's'}`}
        </Badge>
        {data.termMonths > 0 && (
          <Badge borderRadius="full" px={2} fontSize="0.6rem" colorScheme="purple">{`${data.termMonths} mo`}</Badge>
        )}
        <Box flex="1" />
        {/* the reconciliation check — the agreements must add up to what was quoted */}
        {oneTime.reconciles ? (
          <Tooltip label="The agreements' one-time money matches the proposal's one-time total." hasArrow>
            <HStack spacing={1} color="green.400">
              <FiCheckCircle />
              <Text fontSize="xs" fontWeight="semibold">Matches the quote</Text>
            </HStack>
          </Tooltip>
        ) : (
          <Tooltip
            label={`Quoted ${money(oneTime.quoted)} · contracted ${money(oneTime.total)}. Check the agreements before sending.`}
            hasArrow
          >
            <HStack spacing={1} color="orange.400">
              <FiAlertTriangle />
              <Text fontSize="xs" fontWeight="semibold">
                {`Off by ${money(Math.abs(oneTime.difference))}`}
              </Text>
            </HStack>
          </Tooltip>
        )}
      </Flex>

      <Flex wrap="wrap" align="baseline" sx={{ gap: '6px 18px' }}>
        <HStack spacing={2} align="baseline">
          <Text fontSize="sm" fontWeight="bold">{money(oneTime.total)}</Text>
          <Text fontSize="xs" color={sub}>one-time</Text>
        </HStack>
        {part('· subscription', oneTime.subscriptionSetup)}
        {part('· SwitchPilot', oneTime.switchPilotSetup)}

        <Divider orientation="vertical" h="14px" display={{ base: 'none', md: 'block' }} />

        <HStack spacing={2} align="baseline">
          <Text fontSize="sm" fontWeight="bold">{`${money(monthly.total)}/mo`}</Text>
          <Text fontSize="xs" color={sub}>monthly</Text>
        </HStack>
        {part('· RNWY', monthly.license)}
        {part('· SwitchPilot', monthly.switchPilot)}
        {monthly.helpDesk > 0 && (
          <Text fontSize="xs" color={sub} whiteSpace="nowrap">
            {`· Help Desk (${monthly.helpDeskTier}) ${money(monthly.helpDesk)}`}
          </Text>
        )}
      </Flex>
      {/* item 1411 (Chad: "deal total — correct or not correct?") — a Help Desk paper that
          disagrees with the deal is called out right here, not discovered by the customer */}
      {data.helpDeskPaper && !data.helpDeskPaper.matches && (
        <HStack spacing={2} mt={1} align="flex-start">
          <Box color="orange.300" pt="1px"><FiAlertTriangle /></Box>
          <Text fontSize="xs" color="orange.300">{data.helpDeskPaper.note}</Text>
        </HStack>
      )}
    </Box>
  );
};

export default DealTotalsCard;
